/**
 * React Query wrappers for the Vault Documents HTTP surface
 * (Phase 12d-1b).
 *
 * One hook per route. Mutations invalidate the per-Holder branch
 * (`forHolder(holderId)`) so the list / detail / search caches all
 * refresh on any write. Detail queries are keyed by documentId so a
 * patch on /document/X doesn't invalidate the corpus.
 *
 * Consumed by the Vault Documents arche shell + the My Data dual-tab
 * integration.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  api,
  type VaultDocumentDto,
  type VaultDocumentMetadataFilters,
  type VaultDocumentReferenceTargetTypeDto,
  type VaultDocumentSensitivityDto,
  type VaultDocumentStatusDto,
  type VaultHistoryPolicyDto,
  type WorkspaceFileSettingsDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface UseVaultDocumentsListFilters extends VaultDocumentMetadataFilters {
  status?: VaultDocumentStatusDto;
  tags?: string[];
  includeArchived?: boolean;
  includeDeleted?: boolean;
  /** Drives the Trash view. Mutually exclusive with default listing. */
  deletedOnly?: boolean;
  /** `null` = Holder root. `vdf_*` = specific folder. Omit to list
   * across every folder (legacy behavior + search). */
  folderId?: string | null;
}

/** Files per page of the Files list and the Trash ("Load more"). */
export const VAULT_DOCUMENTS_PAGE_SIZE = 100;

/**
 * A folder's files (or the Trash), a page at a time. `data` is every page
 * loaded so far; `total` is how many there are in all, counted by the server
 * with the same filters, so "Load more" and "n of m" are exact.
 */
export function useVaultDocumentsList(
  holderId: string | null,
  filters: UseVaultDocumentsListFilters = {},
) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.vaultDocuments.list(holderId ?? '', filters),
    queryFn: ({ pageParam }) =>
      api.listVaultDocuments(holderId!, {
        ...filters,
        limit: VAULT_DOCUMENTS_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.documents.length, 0);
      return loaded < lastPage.total && lastPage.documents.length > 0 ? loaded : undefined;
    },
    enabled: !!holderId,
  });
  const pages = query.data?.pages;
  return {
    data: pages ? pages.flatMap((page) => page.documents) : undefined,
    total: pages?.[pages.length - 1]?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

/**
 * The workspace's By Type / By Arche summary — every file matching the
 * filters, grouped, independent of how much of the list has loaded. Free
 * metadata aggregation; no entity call.
 */
export function useVaultDocumentGroups<By extends 'kind' | 'arche'>(
  holderId: string | null,
  by: By,
  filters: VaultDocumentMetadataFilters,
  options: { enabled?: boolean } = {},
) {
  // A grouping covers the whole matching set and never the Trash: the route
  // refuses paging, order and the Trash toggles, so they never reach it.
  const {
    sort: _sort,
    direction: _direction,
    deletedOnly: _deletedOnly,
    includeDeleted: _includeDeleted,
    ...groupFilters
  } = filters;
  return useQuery({
    queryKey: queryKeys.vaultDocuments.groups(holderId ?? '', by, groupFilters),
    queryFn: () =>
      (by === 'kind'
        ? api.groupVaultDocumentsByKind(holderId!, groupFilters)
        : api.groupVaultDocumentsByArche(holderId!, groupFilters)) as Promise<
        By extends 'kind'
          ? Awaited<ReturnType<typeof api.groupVaultDocumentsByKind>>
          : Awaited<ReturnType<typeof api.groupVaultDocumentsByArche>>
      >,
    enabled: !!holderId && options.enabled !== false,
  });
}

export function useVaultDocument(documentId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.detail(documentId ?? ''),
    queryFn: () => api.getVaultDocument(documentId!),
    select: (data) => data.document,
    enabled: !!documentId,
  });
}

export function useVaultDocumentVersions(documentId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.versions(documentId ?? ''),
    queryFn: () => api.listVaultDocumentVersions(documentId!),
    select: (data) => data.versions,
    enabled: !!documentId,
  });
}

/** What arches keep for the user outside their files — fetched when asked for. */
export function useVaultArcheData(holderId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.archeData(holderId ?? ''),
    queryFn: () => api.getVaultArcheData(holderId),
    enabled,
  });
}

/** What is using a workspace's file storage — fetched when asked for. */
export function useVaultStorageBreakdown(holderId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.storageBreakdown(holderId ?? ''),
    queryFn: () => api.getVaultStorageBreakdown(holderId!),
    enabled: !!holderId && enabled,
  });
}

// ---------------------------------------------------------------------------
// Version history the user decides (file areas §4.12)
// ---------------------------------------------------------------------------

/** The file's history choice, the workspace default, what applies, and the
 *  history's size. */
export function useVaultDocumentHistory(documentId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.history(documentId ?? ''),
    queryFn: () => api.getVaultDocumentHistory(documentId!),
    enabled: !!documentId,
  });
}

/** Set the file's own choice ('keep' / 'off' / null = workspace default). */
export function useSetVaultDocumentHistoryChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; policy: VaultHistoryPolicyDto | null }) =>
      api.setVaultDocumentHistoryChoice(input.documentId, input.policy),
    onSuccess: (_data, variables) => invalidateDocDetail(qc, variables.documentId),
  });
}

/** Delete one earlier version, or (versionId omitted) all but the current
 *  one. The file's version list, history size and the storage bar refresh. */
export function useDeleteVaultDocumentVersions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; holderId: string; versionId?: string }) =>
      input.versionId
        ? api.deleteVaultDocumentVersion(input.documentId, input.versionId)
        : api.deleteEarlierVaultDocumentVersions(input.documentId),
    onSuccess: (_data, variables) => {
      invalidateDocDetail(qc, variables.documentId);
      qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.storage(variables.holderId) });
    },
  });
}

/** A workspace's version-history default, and whether the caller may change it. */
export function useWorkspaceFileSettings(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.fileSettings(holderId ?? ''),
    queryFn: () => api.getWorkspaceFileSettings(holderId!),
    enabled: !!holderId,
  });
}

export function useUpdateWorkspaceFileSettings(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      patch: Partial<Pick<WorkspaceFileSettingsDto, 'historyEnabled' | 'historyMaxVersions'>>,
    ) => api.updateWorkspaceFileSettings(holderId!, patch),
    onSuccess: (data) => {
      if (!holderId) return;
      qc.setQueryData(queryKeys.vaultDocuments.fileSettings(holderId), data);
      // Every file's "what applies" follows the workspace default.
      qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.details() });
    },
  });
}

/** Fetches a fresh 1-hour signed download URL on demand. Not auto-
 * cached because the URL is short-lived; callers typically trigger
 * this from a button click, then `window.open(url)`. */
export function useVaultDocumentDownload() {
  return useMutation({
    mutationFn: (documentId: string) => api.getVaultDocumentDownloadUrl(documentId),
  });
}

/** Restore an older version to current. `documentId` identifies the
 * chain; `versionId` is the older version to bring forward. Busts the
 * Holder branch (the chain head moved → list + detail + versions all
 * refresh) and the detail of the new head. */
export function useRestoreVaultDocumentVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; versionId: string }) =>
      api.restoreVaultDocumentVersion(input.documentId, input.versionId),
    onSuccess: (data, variables) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
      // The chain the user was viewing also needs its versions list +
      // detail refreshed (the old head is now superseded).
      invalidateDocDetail(qc, variables.documentId);
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.versions(variables.documentId),
      });
    },
  });
}

export interface UseVaultDocumentsSearchParams {
  query: string;
  topK?: number;
  minSimilarity?: number;
  includeContent?: boolean;
}

export function useVaultDocumentsSearch(
  holderId: string | null,
  params: UseVaultDocumentsSearchParams,
  options?: { enabled?: boolean },
) {
  // The query embedding burns a credit per search (AI Markup
  // Invariant), so the consuming component should gate the
  // `enabled` flag — typically tying it to a debounced "submit"
  // rather than every keystroke.
  return useQuery({
    queryKey: queryKeys.vaultDocuments.search(holderId ?? '', params),
    queryFn: () =>
      api.searchVaultDocuments({
        holderId: holderId!,
        query: params.query,
        topK: params.topK,
        minSimilarity: params.minSimilarity,
        includeContent: params.includeContent,
      }),
    select: (data) => data.matches,
    enabled: !!holderId && !!params.query.trim() && options?.enabled !== false,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function invalidateHolderBranch(qc: QueryClient, holderId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.forHolder(holderId) });
}

function invalidateDocDetail(qc: QueryClient, documentId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.detail(documentId) });
}

/**
 * Upload-init mutation. Returns the presigned URL + headers; the
 * component is responsible for the direct S3 PUT and the followup
 * finalize call. We don't combine the three into a single mutation
 * because the PUT is browser-only (the api client can't see the
 * progress events) and finalize wants its own error surface.
 */
export function useVaultDocumentUploadInit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.initVaultDocumentUpload>[0]) =>
      api.initVaultDocumentUpload(input),
    onSuccess: (_data, variables) => {
      // The new pending row already counts toward quota — invalidate
      // the Holder branch so the storage usage widget refreshes.
      invalidateHolderBranch(qc, variables.holderId);
    },
  });
}

export function useVaultDocumentFinalize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; checksumSha256: string }) =>
      api.finalizeVaultDocumentUpload(input.documentId, {
        checksumSha256: input.checksumSha256,
      }),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useUpdateVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      documentId: string;
      updates: Parameters<typeof api.updateVaultDocument>[1];
    }) => api.updateVaultDocument(input.documentId, input.updates),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useArchiveVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.archiveVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useUnarchiveVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.unarchiveVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useSetVaultDocumentSensitivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; sensitivity: VaultDocumentSensitivityDto }) =>
      api.setVaultDocumentSensitivity(input.documentId, input.sensitivity),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useSoftDeleteVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.softDeleteVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useRestoreVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.restoreVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

/** Move a document into a different folder (or root). The server
 * returns the updated row; we bust the Holder branch so source-folder
 * + target-folder + root list caches all refresh. */
export function useMoveVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; targetFolderId: string | null }) =>
      api.moveVaultDocument(input.documentId, input.targetFolderId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

/** Copy a file into a Documents folder of the same workspace. A new file:
 *  the workspace's lists, groups and storage all change. */
export function useCopyVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      documentId: string;
      holderId: string;
      folderId: string | null;
      name?: string;
      onConflict?: 'keep-both' | 'fail';
    }) =>
      api.copyVaultDocument(input.documentId, {
        holderId: input.holderId,
        folderId: input.folderId,
        name: input.name,
        onConflict: input.onConflict,
      }),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
    },
  });
}

/** Apps holding a grant on one document — the detail page's "App access".
 *  Listed on the DOCUMENT's holder: a grant lives where the document lives,
 *  and the server defaults to Personal, which would hide an org document's
 *  grants on the one screen that must show them. */
export function useVaultDocumentAppAccess(documentId: string, holderId: string) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.appAccess(documentId),
    queryFn: async () => {
      const { consents, archeNames } = await api.listVaultArcheConsents({ documentId, holderId });
      return {
        grants: consents.filter((c) => c.status === 'active'),
        archeNames,
      };
    },
  });
}

/**
 * The Arche Access page's data: the ACTIVE workspace's arche grants plus the
 * display names of the documents they name (Vault → Arche Access). The names
 * come back with the grants, resolved server-side for exactly the granted
 * files — a grant with no name has lost its file, and the page says so.
 *
 * Takes the switcher's current holder. The grants list is server-scoped to the
 * holder it is asked for (Personal when omitted). Arche grants live on the
 * workspace the documents are in — an org workspace whenever a member shared
 * its files with an arche — so this must follow the switcher, not pin the
 * caller's Personal holder.
 */
export function useVaultAppAccessList(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.appAccessList(holderId ?? 'none'),
    enabled: holderId !== null,
    queryFn: async () => {
      const consentRes = await api.listVaultArcheConsents({ holderId: holderId as string });
      return {
        consents: consentRes.consents.filter((c) => c.status === 'active'),
        archeNames: consentRes.archeNames,
        documentNames: consentRes.documentNames ?? {},
      };
    },
  });
}

/**
 * A grant shows in TWO places — the file's own "App access" list and the App
 * Access page — so a change made in either must refresh both. `documentId`
 * narrows the per-file half when the caller knows it; without it every open
 * file's list is refreshed (the Arche Access page acts across documents).
 */
function invalidateAppAccess(qc: ReturnType<typeof useQueryClient>, documentId?: string) {
  qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.appAccessLists() });
  qc.invalidateQueries({
    queryKey: documentId
      ? queryKeys.vaultDocuments.appAccess(documentId)
      : queryKeys.vaultDocuments.details(),
  });
}

/** Lower an app's grant on a document to view-only. (Raising it is never done
 *  from the user's management UI — see VaultAccessGrantSheet.) */
export function useMakeVaultArcheConsentViewOnly(documentId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consentId: string) => api.setVaultArcheConsentAccess(consentId, 'read'),
    // Settled, not success: after a FAILURE the list must show what is still
    // true (the app can still edit), not an optimistic guess.
    onSettled: () => invalidateAppAccess(qc, documentId),
  });
}

/** Remove an app's access to a document entirely. */
export function useRevokeVaultArcheConsent(documentId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consentId: string) => api.revokeVaultConsent(consentId),
    onSettled: () => invalidateAppAccess(qc, documentId),
  });
}

/** Who changed this file, newest first (first page). */
export function useVaultDocumentActivity(documentId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.activity(documentId),
    queryFn: () => api.getVaultDocumentActivity(documentId, { limit: 20 }),
    enabled,
  });
}

/** Rebuild the search index after the content was changed in place. */
export function useReindexVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.reindexVaultDocument(documentId),
    onSuccess: (data) => invalidateDocDetail(qc, data.documentId),
  });
}

/** Hard-delete a single soft-deleted document. Caller passes
 * `holderId` so we can bust the per-Holder branch — the response has
 * no document body (the row is gone). */
export function usePurgeVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; holderId: string }) =>
      api.purgeVaultDocument(input.documentId),
    onSuccess: (_data, variables) => {
      invalidateHolderBranch(qc, variables.holderId);
      invalidateDocDetail(qc, variables.documentId);
    },
  });
}

/** Empty trash — server reaps up to the per-call batch cap and
 * returns `{ purgedCount, remainingCount, failures }`. The caller
 * loops while `remainingCount > 0`. */
export function useEmptyVaultTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.emptyVaultTrash(holderId),
    onSuccess: (_data, holderId) => {
      invalidateHolderBranch(qc, holderId);
    },
  });
}

// ---------------------------------------------------------------------------
// High-level "do an upload" composite
// ---------------------------------------------------------------------------

/**
 * Compute SHA-256 of a File client-side. Used by uploaders before
 * calling upload-init (the server signs the presigned PUT with the
 * checksum so S3 rejects mismatched bytes). Returns lowercase hex.
 */
export async function computeFileSha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface PerformUploadInput {
  holderId: string;
  file: File;
  sensitivity?: VaultDocumentSensitivityDto;
  description?: string;
  tags?: string[];
  /** Optional rename for the stored doc; defaults to file.name. */
  displayName?: string;
  /** Target folder for the new document. NULL/omitted = Holder root. */
  folderId?: string | null;
}

/**
 * End-to-end uploader: SHA-256 the file, call upload-init, PUT bytes
 * direct to S3 with the signed headers, then finalize. Returns the
 * finalized document row.
 *
 * Throws on any step failure — caller surfaces the error.
 */
export function useUploadVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PerformUploadInput): Promise<VaultDocumentDto> => {
      const checksumSha256 = await computeFileSha256Hex(input.file);
      const init = await api.initVaultDocumentUpload({
        holderId: input.holderId,
        name: input.displayName ?? input.file.name,
        filename: input.file.name,
        mimeType: input.file.type || 'application/octet-stream',
        sizeBytes: input.file.size,
        checksumSha256,
        sensitivity: input.sensitivity,
        description: input.description,
        tags: input.tags,
        folderId: input.folderId ?? null,
      });

      // Direct-to-S3 PUT. The signed URL pins ContentType +
      // ContentLength + checksum + the pending tag — any mismatch
      // fails the S3 signature.
      const putResponse = await fetch(init.uploadUrl, {
        method: 'PUT',
        headers: init.requiredHeaders,
        body: input.file,
      });
      if (!putResponse.ok) {
        throw new Error(`S3 PUT failed (${putResponse.status}): the upload didn't complete.`);
      }

      const finalized = await api.finalizeVaultDocumentUpload(init.document.documentId, {
        checksumSha256,
      });
      return finalized.document;
    },
    onSuccess: (document) => {
      invalidateHolderBranch(qc, document.holderId);
    },
  });
}

// ---------------------------------------------------------------------------
// Cross-arche attachment (Phase 12e-1)
// ---------------------------------------------------------------------------

export function useVaultDocumentAttachments(documentId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.attachmentsForDocument(documentId ?? ''),
    queryFn: () => api.listVaultDocumentAttachmentsForDocument(documentId!),
    select: (data) => data.references,
    enabled: !!documentId,
  });
}

export function useVaultDocumentsForTarget(
  regardingType: VaultDocumentReferenceTargetTypeDto | null,
  regardingId: string | null,
) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.attachmentsForTarget(regardingType ?? '', regardingId ?? ''),
    queryFn: () => api.listVaultDocumentsForTarget(regardingType!, regardingId!),
    enabled: !!regardingType && !!regardingId,
  });
}

export function useAttachVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      documentId: string;
      regardingType: VaultDocumentReferenceTargetTypeDto;
      regardingId: string;
    }) =>
      api.attachVaultDocument(input.documentId, {
        regardingType: input.regardingType,
        regardingId: input.regardingId,
      }),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForDocument(variables.documentId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForTarget(
          variables.regardingType,
          variables.regardingId,
        ),
      });
      // Also bust the holder branch so list views re-fetch (the
      // document itself didn't change, but the consumer's UI may
      // also surface attachment counts).
      invalidateHolderBranch(qc, data.reference.holderId);
    },
  });
}

export function useDetachVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; referenceId: string }) =>
      api.detachVaultDocumentReference(input.documentId, input.referenceId),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForDocument(variables.documentId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForTarget(
          data.reference.regardingType,
          data.reference.regardingId,
        ),
      });
      invalidateHolderBranch(qc, data.reference.holderId);
    },
  });
}

// ---------------------------------------------------------------------------
// Storage usage (review fix #9)
// ---------------------------------------------------------------------------

export function useVaultDocumentsStorage(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.storage(holderId ?? ''),
    queryFn: () => api.getVaultDocumentsStorage(holderId!),
    // Returns { storage, principal }. The free-tier cap is enforced
    // at the billing-principal level — UI usage bars should bind to
    // `principal` for the actual quota; `storage` is the per-Holder
    // breakdown.
    enabled: !!holderId,
  });
}

// ---------------------------------------------------------------------------
// Folders (vault_document_folders)
// ---------------------------------------------------------------------------

/** Flat list of every folder in the Holder. Consumer assembles the
 * tree from `parentFolderId` pointers. Single cache slot — folder
 * lists are small and the My Data UI needs the whole tree on render. */
export function useVaultDocumentFolders(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.folders(holderId ?? ''),
    queryFn: () => api.listVaultDocumentFolders(holderId!),
    select: (data) => data.folders,
    enabled: !!holderId,
  });
}

export function useCreateVaultDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { holderId: string; parentFolderId?: string | null; name: string }) =>
      api.createVaultDocumentFolder(input),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.folder.holderId);
    },
  });
}

export function useRenameVaultDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { folderId: string; name: string }) =>
      api.renameVaultDocumentFolder(input.folderId, input.name),
    onSuccess: (data) => {
      // Rename rewrites `path` on every descendant folder + every
      // descendant document, so the whole Holder branch may shift —
      // bust it all.
      invalidateHolderBranch(qc, data.folder.holderId);
    },
  });
}

/** Re-parent a folder (`parentFolderId: null` = Holder root). Like
 * rename, the server rewrites `path` on the folder and every
 * descendant folder, so the whole Holder branch is invalidated rather
 * than surgically patching two cache entries. */
export function useMoveVaultDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { folderId: string; parentFolderId: string | null }) =>
      api.moveVaultDocumentFolder(input.folderId, input.parentFolderId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.folder.holderId);
    },
  });
}

/** Delete a user-managed folder. Caller passes `holderId` so we can
 * invalidate the per-Holder branch — the 204 response carries no
 * body to derive it from. */
export function useDeleteVaultDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { folderId: string; holderId: string; force?: boolean }) =>
      api.deleteVaultDocumentFolder(input.folderId, { force: input.force }),
    onSuccess: (_data, variables) => {
      invalidateHolderBranch(qc, variables.holderId);
    },
  });
}

/** Self-heal mutation — re-runs server-side `ensureSystemFolders` for a
 * Holder. The route is idempotent (ON CONFLICT DO NOTHING on the
 * `(holder_id, path)` unique index), so calling it on a Holder that
 * already has the scaffold is a no-op insert + a single SELECT.
 *
 * Use case: Holders created before the folder-substrate commit
 * (`e80e6677`, 2026-05-18) never had `ensureSystemFolders` called at
 * provisioning time, so they have zero folders. The DocumentsListPage
 * fires this once per Holder when the folders query lands empty —
 * patching legacy Holders the moment a user visits them. New Holders
 * already get the scaffold inside their creation transaction; this
 * hook is the migration path, not the steady-state path. */
export function useEnsureVaultSystemFolders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.ensureVaultSystemFolders(holderId),
    onSuccess: (_data, holderId) => {
      // Bust the whole Holder branch so the folders query refetches and
      // the documents-list query picks up the new folder_id values it
      // can now filter on.
      invalidateHolderBranch(qc, holderId);
    },
  });
}
